import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Clock, Camera, FileText, Trophy, ShieldCheck, Calendar, Newspaper, Loader2, Contact, Upload } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Greeting } from "@/components/Greeting";
import FooterHero from "@/components/FooterHero";
import "@/components/FooterHero.css";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/a4e7a419-71b0-4f59-8501-640e90382c41/images/dd8635bc0064e4c3e766456c157833579f292653a2c73bf499b4576d7441e265.jpeg";

const FOOTER_SLIDESHOW = [
  "https://static.prod-images.emergentagent.com/jobs/a4e7a419-71b0-4f59-8501-640e90382c41/images/8c481a12161d5d6e8c66eaf357d0199d60af674d27695e36a50eeecba9b264c9.jpeg",
  HERO_BG,
  "https://static.prod-images.emergentagent.com/jobs/a4e7a419-71b0-4f59-8501-640e90382c41/images/9e8ad3797c2c7425be558e629368aa6547f53633483cd8de2367efecda23d8c4.jpeg",
];

const TYPES = [
  {
    key: "project",
    label: "Project",
    sub: "Full case-study workflow with photos & key facts",
    icon: FileText,
    accent: "bg-sky-100 text-sky-700",
  },
  {
    key: "award",
    label: "Award",
    sub: "Wins, shortlists, finalist & highly-commended",
    icon: Trophy,
    accent: "bg-sky-100 text-sky-700",
  },
  {
    key: "certification",
    label: "Certification / Accreditation",
    sub: "ISO, accreditations and renewals",
    icon: ShieldCheck,
    accent: "bg-emerald-100 text-emerald-700",
  },
  {
    key: "event",
    label: "Event",
    sub: "Conferences, exhibitions, team & charity events",
    icon: Calendar,
    accent: "bg-sky-100 text-sky-700",
  },
  {
    key: "media",
    label: "Media Coverage & News",
    sub: "Press, articles, podcasts, social mentions",
    icon: Newspaper,
    accent: "bg-rose-100 text-rose-700",
  },
  {
    key: "cv",
    label: "Staff CV",
    sub: "Tender-ready CVs for bids & frameworks",
    icon: Contact,
    accent: "bg-indigo-100 text-indigo-700",
  },
  {
    key: "upload_case_study",
    label: "Upload existing case study",
    sub: "Got a written case study? AI fills the project form for you",
    icon: Upload,
    accent: "bg-amber-100 text-amber-700",
  },
  {
    key: "photos_first",
    label: "Photos First",
    sub: "On site? Upload photos now — details later",
    icon: Camera,
    accent: "bg-gray-100 text-gray-700",
  },
];

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [draftPrompt, setDraftPrompt] = useState(null);
  const [busyKey, setBusyKey] = useState("");
  const uploadRef = useRef(null);

  const uploadCaseStudy = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusyKey("upload_case_study");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/projects/parse-upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      localStorage.setItem("rcw_draft_token", data.draft_token);
      toast.success(`Imported "${data.project_name}" — review each step and add photos`);
      navigate(`/submit/${data.draft_token}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not read that document");
    } finally {
      setBusyKey("");
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("rcw_draft_token");
    if (!token) return;
    api
      .get(`/projects/draft/${token}`)
      .then((r) => {
        if (r.data?.status === "Draft" || r.data?.status === "Draft Photo Submission") {
          setDraftPrompt(r.data);
        } else {
          localStorage.removeItem("rcw_draft_token");
        }
      })
      .catch(() => localStorage.removeItem("rcw_draft_token"));
  }, []);

  const start = async (entryType) => {
    if (entryType === "upload_case_study") {
      uploadRef.current?.click();
      return;
    }
    setBusyKey(entryType);
    localStorage.removeItem("rcw_draft_token");
    if (entryType === "cv") {
      navigate("/cvs/new");
      setBusyKey("");
      return;
    }
    try {
      const isPhotosFirst = entryType === "photos_first";
      const { data } = await api.post("/projects", {
        submission_type: isPhotosFirst ? "photo_first" : (entryType === "project" ? "full" : "short"),
        entry_type: isPhotosFirst ? "project" : entryType,
        // pre-fill submitter so the first step shows their name/email
        submitter_name: user?.name || "",
        submitter_email: user?.email || "",
      });
      localStorage.setItem("rcw_draft_token", data.draft_token);
      if (isPhotosFirst) {
        navigate(`/submit-photos/${data.draft_token}`);
      } else if (entryType === "project") {
        navigate(`/submit/${data.draft_token}`);
      } else {
        navigate(`/submit-${entryType}/${data.draft_token}`);
      }
    } finally {
      setBusyKey("");
    }
  };

  const continueDraft = () => {
    const t = draftPrompt;
    const et = t?.entry_type || "project";
    if (et === "project") {
      navigate(t?.submission_type === "photo_first"
        ? `/submit-photos/${t.draft_token}`
        : `/submit/${t.draft_token}`);
    } else {
      navigate(`/submit-${et}/${t.draft_token}`);
    }
  };

  const discardDraft = () => { localStorage.removeItem("rcw_draft_token"); setDraftPrompt(null); };

  return (
    <Layout hideFooter>
      <section className="relative overflow-hidden bg-[#0A2540] text-white">
        <div className="absolute inset-0 opacity-40"
          style={{ backgroundImage: `url('${HERO_BG}')`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540]/95 via-[#0D3A63]/75 to-[#2899FF]/25" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A2540]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-500/15 text-sky-400 text-xs font-semibold tracking-[0.18em] uppercase">
            <span className="h-1.5 w-1.5 rounded-full bg-sky-400" /> HTM Project Hub
          </div>
          <Greeting className="mt-5" />
          <h1 className="mt-3 font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl">
            What would you like<br />
            <span className="text-sky-400">to submit?</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-gray-300 max-w-2xl leading-relaxed">
            Choose what you want to capture and we&apos;ll guide you through the quickest path.
            Projects get a full case-study workflow; everything else is a short 3-step capture.
          </p>

          {draftPrompt ? (
            <div className="mt-8 rcw-card p-5 bg-white/95 text-gray-900 max-w-2xl" data-testid="draft-recovery-card">
              <div className="flex items-start gap-3">
                <FileText className="h-5 w-5 text-sky-600 mt-0.5" />
                <div className="flex-1">
                  <div className="font-display font-semibold text-lg">Continue your draft?</div>
                  <div className="text-sm text-gray-600 mt-1">
                    {draftPrompt.project_name || draftPrompt.type_data?.title || draftPrompt.type_data?.award_name || draftPrompt.type_data?.event_name || draftPrompt.type_data?.certification_name || "Untitled draft"}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex flex-col sm:flex-row gap-2">
                <button onClick={continueDraft} className="rcw-btn-amber" data-testid="draft-continue">
                  Continue <ArrowRight className="h-4 w-4" />
                </button>
                <button onClick={discardDraft} className="rcw-btn-ghost" data-testid="draft-discard">
                  Start something new
                </button>
              </div>
            </div>
          ) : null}

          <div className="mt-6 inline-flex items-center gap-2 text-sm text-gray-400">
            <Clock className="h-4 w-4" /> Most short submissions take under 3 minutes.
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <input ref={uploadRef} type="file" accept=".pdf,.docx,.txt,.md" className="hidden"
          onChange={uploadCaseStudy} data-testid="upload-case-study-input" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {TYPES.map((t) => {
            const Icon = t.icon;
            const featured = t.key === "project";
            return (
              <button
                key={t.key}
                onClick={() => start(t.key)}
                disabled={busyKey !== ""}
                className={`group text-left p-5 sm:p-6 rounded-2xl border transition shadow-sm hover:shadow-md ${
                  featured ? "bg-gray-900 text-white border-gray-900 lg:col-span-2" : "bg-white text-gray-900 border-gray-200 hover:border-sky-300"
                }`}
                data-testid={`start-${t.key}`}
              >
                <div className={`h-10 w-10 rounded-xl inline-flex items-center justify-center ${
                  featured ? "bg-sky-400 text-gray-900" : t.accent
                }`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className={`mt-4 font-display font-semibold text-lg sm:text-xl ${featured ? "text-sky-300" : "text-gray-900"}`}>
                  {t.label}
                </div>
                <div className={`mt-1 text-sm ${featured ? "text-gray-300" : "text-gray-600"}`}>
                  {t.sub}
                </div>
                <div className={`mt-4 inline-flex items-center gap-1 text-sm font-medium ${featured ? "text-sky-300" : "text-sky-600"}`}>
                  {busyKey === t.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition" />}
                  Start
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <FooterHero
        onStartFull={() => start("project")}
        onStartPhotos={() => start("photos_first")}
        images={FOOTER_SLIDESHOW}
      />
    </Layout>
  );
}
